
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  created_at: string;
}

export const useAdminUsers = () => {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchAdminUsers = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get users with admin role from user_roles table
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'admin');

      if (roleError) throw roleError;
      
      if (!roleData || roleData.length === 0) {
        setAdminUsers([]);
        setIsLoading(false);
        return;
      }
      
      // Get users from auth.users using their IDs
      const adminUserIds = roleData.map(role => role.user_id);
      
      // Instead of using auth.admin.listUsers, we'll use a simpler approach
      // that doesn't require admin privileges
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // For security reasons, we can only get the current user's data
      // So we'll display a message about permissions
      if (adminUserIds.includes(userData.user?.id)) {
        // We can at least show the current user if they are an admin
        setAdminUsers([{
          id: userData.user.id,
          email: userData.user.email || '',
          name: userData.user.user_metadata?.full_name || 'Current User',
          created_at: userData.user.created_at || ''
        }]);
      } else {
        // If the current user isn't an admin, we can't show any admin users
        setAdminUsers([]);
      }
    } catch (error: any) {
      console.error('Error fetching admin users:', error);
      setError(error.message || 'Failed to fetch admin users');
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch admin users',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  return { adminUsers, isLoading, error, fetchAdminUsers };
};
